<template>
  <div class="card mb-4">
    <div class="card-header">
      <div class="row flex-between-center">
        <div class="col-auto">
          <h5 class="fs-6 mb-0">
            Daily Trend Flips
            <small v-show="runDate" class="text-muted ms-1">{{ runDate }}</small>
          </h5>
        </div>
      </div>
    </div>
    <div class="card-body p-0">
      <div v-if="results.length > 0" class="table-responsive scrollbar">
        <table class="table table-hover table-sm table-striped fs-10 mb-0 overflow-hidden">
          <thead class="bg-200">
            <tr>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('symbolTitle')">
                Asset <span v-html="getSortIcon('symbolTitle')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('symbol')">
                Symbol <span v-html="getSortIcon('symbol')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('trendTo')">
                Trend <span v-html="getSortIcon('trendTo')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('trendChanged')">
                Trend Flipped <span v-html="getSortIcon('trendChanged')"></span>
              </th> 
              <th v-if="detector === 'b2s'" class="text-900 align-middle white-space-nowrap">
                Range
              </th>
              <th v-if="detector === 'b2s'" class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('currentStatus')">
                Status <span v-html="getSortIcon('currentStatus')"></span>
              </th>
              <th v-if="detector === 'b2s'" class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('tradeDate')">
                Trade Date <span v-html="getSortIcon('tradeDate')"></span>
              </th>
              <th v-if="detector === 'b2s'" class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('pnlPct')">
                P&amp;L % <span v-html="getSortIcon('pnlPct')"></span>
              </th>
            </tr>
          </thead>
          <tbody class="list">
            <tr v-for="item in sortedResults" :key="item.symbol">
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <span class="fw-bold">{{ item.symbolTitle }}</span>
              </td>
              <td class="py-2 pe-2 align-middle white-space-nowrap"><a :href="'https://www.tradingview.com/chart/?symbol=' + item.symbol" target="_blank" class="text-primary">{{ item.symbol }}</a></td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <span class="fw-bold" :class="getTrendBadgeClass(item.trendTo)">{{ item.trendFrom }} → {{ item.trendTo }}</span>
              </td>
              <td class="py-2 pe-2_ align-middle white-space-nowrap">{{ item.trendChanged || '-' }}</td>
              <td v-if="detector === 'b2s'" class="py-2 align-middle white-space-nowrap text-muted">
                {{ formatRange(item.rangeLow, item.rangeHigh) }}
              </td>
              <td v-if="detector === 'b2s'" class="py-2 pe-2 align-middle white-space-nowrap">
                <span :class="item.currentStatus === 'OPEN' ? 'text-success' : 'text-muted'">{{ item.currentStatus }}</span>
              </td>
              <td v-if="detector === 'b2s'" class="py-2 pe-2 align-middle white-space-nowrap">{{ item.tradeDate || '-' }}</td>
              <td v-if="detector === 'b2s'" class="py-2 pe-2_ align-middle white-space-nowrap" :class="getPnlClass(item.pnlPct)">
                {{ formatPnl(item.pnlPct) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-center py-4 text-muted">
        No results for the selected filters.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrendRadarTable',
  props: {
    results: {
      type: Array,
      required: true
    },
    runDate: {
      type: String,
      default: null
    },
    detector: {
      type: String,
      default: 'gc'
    }
  },
  data () {
    return {
      sortBy: 'trendChanged',
      sortDirection: 'desc'
    }
  },
  computed: {
    sortedResults () {
      const sorted = [...this.results]
      const field = this.sortBy
      const dir = this.sortDirection === 'asc' ? 1 : -1

      sorted.sort((a, b) => {
        let valA = a[field]
        let valB = b[field]

        // Handle nulls
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1

        // Numeric sort for pnlPct
        if (field === 'pnlPct') {
          return (valA - valB) * dir
        }

        // String sort
        valA = String(valA).toLowerCase()
        valB = String(valB).toLowerCase()
        if (valA < valB) return -1 * dir
        if (valA > valB) return 1 * dir
        return 0
      })

      return sorted
    }
  },
  methods: {
    sortTable (field) {
      if (this.sortBy === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortDirection = 'asc'
      }
    },
    getSortIcon (field) {
      if (this.sortBy !== field) return ''
      return this.sortDirection === 'asc' ? '&#9650;' : '&#9660;'
    },
    getTrendBadgeClass (trend) {
      if (trend === 'Green') return 'badge bg-success'
      if (trend === 'Red') return 'badge bg-danger'
      return 'badge bg-secondary'
    },
    getPnlClass (pnl) {
      if (pnl == null) return 'text-muted'
      return pnl >= 0 ? 'text-success' : 'text-danger'
    },
    formatPnl (pnl) {
      if (pnl == null) return '-'
      const sign = pnl >= 0 ? '+' : ''
      return sign + pnl.toFixed(2) + '%'
    },
    formatRange (low, high) {
      if (low == null || high == null) return '-'
      return low.toLocaleString('en-US') + ' - ' + high.toLocaleString('en-US')
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}
.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
.table tr th:last-child {
  text-align: right;
}
.table tr td:last-child {
  text-align: right;
}
</style>
